import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../../supabaseClient';
import Path from '../../components/path/path';
import './MyOrders.css';

const MyOrders = () => {
    const [orders, setOrders] = useState([]);
    const [expandedOrders, setExpandedOrders] = useState({});
    const [loading, setLoading] = useState(true);
    const PAYBILL_NUMBER = '542542';
    const ACCOUNT_NUMBER = '07609030756150';
    const BANK_NAME = 'I&M Bank';

    useEffect(() => {
        loadOrders();
        // Poll for updates every 30 seconds
        const interval = setInterval(loadOrders, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadOrders = async () => {
        try {
            // Get orders from localStorage
            const savedOrders = JSON.parse(localStorage.getItem('thinktwice_orders') || '[]');
            
            if (savedOrders.length === 0) {
                setOrders([]);
                setLoading(false);
                return;
            }

            // Fetch latest status from Supabase for each order
            const orderIds = savedOrders.map(o => o.id);
            const { data: dbOrders } = await supabase
                .from('orders')
                .select('id, status, payment_reference, payment_date')
                .in('id', orderIds);

            // Merge localStorage data with database status
            const mergedOrders = savedOrders.map(localOrder => {
                const dbOrder = dbOrders?.find(db => db.id === localOrder.id);
                return {
                    ...localOrder,
                    status: dbOrder?.status || localOrder.status,
                    paymentReference: dbOrder?.payment_reference,
                    paymentDate: dbOrder?.payment_date
                };
            });

            // Update localStorage with latest status
            localStorage.setItem('thinktwice_orders', JSON.stringify(mergedOrders));
            setOrders(mergedOrders);
            setLoading(false);
        } catch (error) {
            console.error('Error loading orders:', error);
            // Fallback to localStorage only
            const savedOrders = JSON.parse(localStorage.getItem('thinktwice_orders') || '[]');
            setOrders(savedOrders);
            setLoading(false);
        }
    };

    const toggleOrderDetails = (orderId) => {
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending_payment': { text: 'Pending Payment', color: 'warning', icon: '⏳' },
            'paid': { text: 'Paid', color: 'success', icon: '✅' },
            'processing': { text: 'Processing', color: 'info', icon: '📦' },
            'shipped': { text: 'Shipped', color: 'primary', icon: '🚚' },
            'delivered': { text: 'Delivered', color: 'success', icon: '✅' },
            'cancelled': { text: 'Cancelled', color: 'danger', icon: '❌' },
            'payment_failed': { text: 'Payment Failed', color: 'danger', icon: '❌' }
        };
        
        const badge = badges[status] || { text: status, color: 'secondary', icon: '📋' };
        return (
            <span className={`status-badge ${badge.color}`}>
                <span className="status-icon">{badge.icon}</span>
                {badge.text}
            </span>
        );
    };

    const clearOrders = () => {
        if (window.confirm('Are you sure you want to clear all order history? This cannot be undone.')) {
            localStorage.removeItem('thinktwice_orders');
            setOrders([]);
        }
    };

    if (loading) {
        return (
            <>
                <Helmet>
                    <title>ThinkTwiceOnline Store | My Orders</title>
                </Helmet>
                <section id="my-orders">
                    <div className="container my-5">
                        <Path pathName="My Orders" />
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Loading your orders...</p>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    if (orders.length === 0) {
        return (
            <>
                <Helmet>
                    <title>ThinkTwiceOnline Store | My Orders</title>
                </Helmet>
                <section id="my-orders">
                    <div className="container my-5">
                        <Path pathName="My Orders" />
                        <div className="empty-orders">
                            <i className="bi bi-inbox"></i>
                            <h2>No Orders Yet</h2>
                            <p>You haven't placed any orders yet. Start shopping to see your orders here!</p>
                            <a href="/home" className="btn-primary">Start Shopping</a>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>ThinkTwiceOnline Store | My Orders</title>
            </Helmet>
            <section id="my-orders">
                <div className="container my-5">
                    <Path pathName="My Orders" />
                    
                    <div className="orders-header">
                        <h2>My Orders</h2>
                        <div className="orders-actions">
                            <button onClick={loadOrders} className="btn-refresh">
                                <i className="bi bi-arrow-clockwise"></i> Refresh
                            </button>
                            <button onClick={clearOrders} className="btn-clear">
                                <i className="bi bi-trash"></i> Clear History
                            </button>
                        </div>
                    </div>

                    <div className="orders-list">
                        {orders.map((order) => {
                            const isExpanded = expandedOrders[order.id];
                            
                            return (
                                <div key={order.id} className="order-card">
                                    <div className="order-card-header" onClick={() => toggleOrderDetails(order.id)}>
                                        <div className="order-card-main">
                                            <div className="order-info">
                                                <h3>Order #{order.orderNumber}</h3>
                                                <p className="order-date">
                                                    {new Date(order.createdAt).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </p>
                                            </div>
                                            <div className="order-summary">
                                                <div className="order-items-count">
                                                    <i className="bi bi-bag-check"></i>
                                                    <span>{order.orderItems?.length || 0} item(s)</span>
                                                </div>
                                                <div className="order-amount">
                                                    <span className="amount-label">Total</span>
                                                    <span className="amount-value">KSh {order.totalAmount?.toLocaleString()}</span>
                                                </div>
                                                <div className="order-status-section">
                                                    {getStatusBadge(order.status)}
                                                </div>
                                            </div>
                                        </div>
                                        <button className="btn-expand">
                                            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className="order-card-details">
                                            <div className="details-grid">
                                                {/* Customer Details */}
                                                <div className="detail-section">
                                                    <h4><i className="bi bi-person-circle"></i> Customer Information</h4>
                                                    <div className="detail-content">
                                                        <p><strong>Name:</strong> {order.customerName}</p>
                                                        <p><strong>Phone:</strong> <a href={`tel:${order.customerPhone}`}>{order.customerPhone}</a></p>
                                                        {order.customerEmail && (
                                                            <p><strong>Email:</strong> {order.customerEmail}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Delivery Details */}
                                                <div className="detail-section">
                                                    <h4><i className="bi bi-truck"></i> Delivery Information</h4>
                                                    <div className="detail-content">
                                                        <p><strong>County:</strong> {order.deliveryAddress?.county}</p>
                                                        <p><strong>City:</strong> {order.deliveryAddress?.city}</p>
                                                        <p><strong>Address:</strong> {order.deliveryAddress?.address}</p>
                                                        {order.deliveryAddress?.notes && (
                                                            <p><strong>Notes:</strong> <em>{order.deliveryAddress.notes}</em></p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Payment Details */}
                                                <div className="detail-section">
                                                    <h4><i className="bi bi-credit-card"></i> Payment Information</h4>
                                                    <div className="detail-content">
                                                        <p><strong>Payment Method:</strong> {order.status === 'paid' ? 'M-Pesa STK Push' : 'M-Pesa Paybill'}</p>
                                                        {order.status !== 'paid' && (
                                                            <>
                                                                <p><strong>Paybill Number:</strong> {PAYBILL_NUMBER}</p>
                                                                <p><strong>Account Number:</strong> {ACCOUNT_NUMBER}</p>
                                                                <p><strong>Bank:</strong> {BANK_NAME}</p>
                                                            </>
                                                        )}
                                                        {order.paymentReference && (
                                                            <p><strong>M-Pesa Ref:</strong> <code>{order.paymentReference}</code></p>
                                                        )}
                                                        {order.paymentDate && (
                                                            <p><strong>Payment Date:</strong> {new Date(order.paymentDate).toLocaleString()}</p>
                                                        )}
                                                        {order.status === 'pending_payment' && (
                                                            <div className="payment-reminder">
                                                                <p><strong>⚠️ Payment Pending</strong></p>
                                                                <p>Please pay KSh {order.totalAmount?.toLocaleString()} to Paybill {PAYBILL_NUMBER} (Account {ACCOUNT_NUMBER})</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Order Items */}
                                            <div className="detail-section full-width">
                                                <h4><i className="bi bi-bag-check"></i> Order Items ({order.orderItems?.length || 0})</h4>
                                                <div className="order-items-list">
                                                    {order.orderItems?.map((item, idx) => (
                                                        <div key={idx} className="order-item">
                                                            <img 
                                                                src={item.productImg || '/assets/images/placeholder.png'} 
                                                                alt={item.productName}
                                                                onError={(e) => e.target.src = '/assets/images/user.png'}
                                                            />
                                                            <div className="item-info">
                                                                <h5>{item.productName}</h5>
                                                                <p>Qty: {item.productQuantity} × KSh {item.productPrice?.toLocaleString()}</p>
                                                            </div>
                                                            <div className="item-total">
                                                                KSh {(item.productQuantity * item.productPrice).toLocaleString()}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
};

export default MyOrders;
